import * as THREE from "three";
import { animateProp } from "./props";

/**
 * PropField — every road prop built by `buildEntityProp` (trucks, movers, ramps, barriers, pickups, keys, boxes,
 * letters, tokens, bridge decks, gate walls, street props) drawn through instancing: one InstancedMesh per distinct
 * part (same geometry, material, shadow flags and sort order), shared by every prop key that has that part. The
 * matrices are rebuilt every frame from the live entities, so nothing is pooled, moved or released per object.
 *
 *   const field = new PropField(scene);
 *   const proto = field.proto(key, kind, preparedObject, dangerBox?);   // once per prop key (a prepared prototype)
 *   field.begin(clock);                                                 // each frame
 *   field.place(proto, id, x, y, z, entity);                            // each live prop (its root's position)
 *   field.end();                                                        // matrices, counts, bounding spheres
 *
 * The look is the per-object one: each part keeps its prototype's matrix under the root, its material (as prepared
 * by the scene), castShadow / receiveShadow, renderOrder and the group order three.js would give it. Animated props
 * (pickups: float/spin/glow/flames; barriers and gate walls: blinking lamps) run `animateProp` on the prototype once
 * per instance and read the result. A part hidden by a blink is written as a zero-scale matrix. Glow sprites
 * (HaloSprite) become instanced camera-facing quads with the same push behind their anchor.
 */

interface Batch {
  mesh: THREE.InstancedMesh;
  count: number;
  capacity: number;
  /** how far the part reaches beyond its instance's origin (bounding sphere) */
  reach: number;
  minX: number; minY: number; minZ: number; maxX: number; maxY: number; maxZ: number;
  readonly bounds: THREE.Sphere;
}

interface ProtoPart {
  batch: Batch;
  node: THREE.Object3D;
  /** node.matrixWorld in the prototype's space (static parts) */
  local: Float32Array;
  /** the part and its ancestors below the root, when one of them blinks */
  chain?: THREE.Object3D[];
}

export interface PropProto {
  readonly key: string;
  readonly kind: string;
  readonly root: THREE.Object3D;
  /** "pickup" (matrices move) | "blink" (lamps toggle) | "" */
  readonly anim: string;
  readonly parts: ProtoPart[];
  /** the high-contrast box over the collision box */
  readonly danger: boolean;
}

/** what place() reads from an entity (the danger box's size) */
interface Box { width: number; height: number; depth: number; yBase: number; }

const LAMP = /^lamp\d$/;
const _e = new Float32Array(16);
const _v = new THREE.Vector3();

/** The glow quads: a HaloSprite per instance (centre and size from the instance matrix), as CoinField draws them. */
function spriteMaterial(source: THREE.SpriteMaterial, push: number): THREE.ShaderMaterial {
  const material = new THREE.ShaderMaterial({
    uniforms: THREE.UniformsUtils.merge([THREE.UniformsLib.fog, { map: { value: null }, color: { value: new THREE.Color() }, opacity: { value: 1 }, push: { value: 0 } }]),
    vertexShader: /* glsl */`
      #include <common>
      #include <fog_pars_vertex>
      uniform float push;
      varying vec2 vUv;
      void main() {
        vUv = uv;
        mat4 m = modelMatrix * instanceMatrix;
        vec4 centre = m * vec4(0.0, 0.0, 0.0, 1.0);
        vec2 size = vec2(length(m[0].xyz), length(m[1].xyz));
        centre.xyz += normalize(centre.xyz - cameraPosition) * push;
        vec4 mvPosition = viewMatrix * centre;
        mvPosition.xy += position.xy * size;
        gl_Position = projectionMatrix * mvPosition;
        #include <fog_vertex>
      }`,
    fragmentShader: /* glsl */`
      #include <common>
      #include <fog_pars_fragment>
      uniform sampler2D map;
      uniform vec3 color;
      uniform float opacity;
      varying vec2 vUv;
      void main() {
        vec4 texel = texture2D(map, vUv);
        gl_FragColor = vec4(color * texel.rgb, opacity * texel.a);
        #include <colorspace_fragment>
        #include <fog_fragment>
      }`,
    transparent: source.transparent,
    depthWrite: source.depthWrite,
    depthTest: source.depthTest,
    blending: source.blending,
    fog: source.fog
  });
  material.uniforms.map.value = source.map;
  (material.uniforms.color.value as THREE.Color).copy(source.color);
  material.uniforms.opacity.value = source.opacity;
  material.uniforms.push.value = push;
  return material;
}

export class PropField {
  readonly root = new THREE.Group();
  private batches = new Map<string, Batch>();
  private list: Batch[] = [];
  private groups = new Map<number, THREE.Group>();
  private protos = new Map<string, PropProto>();
  private sprites = new Map<string, THREE.ShaderMaterial>();
  private quad = new THREE.PlaneGeometry(1, 1);
  private danger?: Batch;
  private clock = 0;

  constructor(private dangerGeometry: THREE.BufferGeometry, private dangerMaterial: THREE.Material) {
    this.root.name = "props";
    this.root.matrixAutoUpdate = false;
  }

  /** a prototype already made for this key */
  get(key: string): PropProto | undefined { return this.protos.get(key); }

  /**
   * Registers a prepared prop (its root at the origin, unscaled, not in the scene) under `key`: every visible mesh and
   * sprite becomes a part of a shared batch. `danger`: draw the high-contrast box for entities of this key.
   */
  proto(key: string, kind: string, root: THREE.Object3D, danger: boolean): PropProto {
    const known = this.protos.get(key);
    if (known) return known;
    root.position.set(0, 0, 0);
    root.updateMatrixWorld(true);
    const anim = (root.userData.anim as string | undefined) ?? "";
    const parts: ProtoPart[] = [];
    const animated = anim === "pickup";
    root.traverse((o) => {
      if (o === root) return;
      const sprite = (o as THREE.Sprite).isSprite === true;
      const mesh = (o as THREE.Mesh).isMesh === true;
      if (!sprite && !mesh) return;
      let hidden = false, blinks = false;
      const chain: THREE.Object3D[] = [];
      for (let p: THREE.Object3D | null = o; p && p !== root; p = p.parent) {
        chain.push(p);
        if (anim === "blink" && LAMP.test(p.name)) blinks = true;
        else if (!p.visible) hidden = true;
      }
      if (hidden) return;                                          // never shown (e.g. the outline's own flames)
      // three.js sorts by the renderOrder of the nearest Group above an object first ("group order")
      let groupOrder = 0;
      for (let p = o.parent; p; p = p.parent) if ((p as THREE.Group).isGroup) { groupOrder = p.renderOrder; break; }
      const local = new Float32Array(o.matrixWorld.elements);
      let batch: Batch;
      if (sprite) {
        const s = o as THREE.Sprite;
        const push = (o as THREE.Sprite & { push?: number }).push ?? 0;
        const id = `s|${s.material.uuid}|${push}|${o.renderOrder}|${groupOrder}`;
        batch = this.batch(id, () => {
          let material = this.sprites.get(`${s.material.uuid}|${push}`);
          if (!material) { material = spriteMaterial(s.material, push); this.sprites.set(`${s.material.uuid}|${push}`, material); }
          return { geometry: this.quad, material, cast: false, receive: false, renderOrder: o.renderOrder, groupOrder, name: kind };
        });
        _v.setFromMatrixScale(o.matrixWorld);
        batch.reach = Math.max(batch.reach, 0.75 * Math.max(_v.x, _v.y, animated ? 2.4 : 0) + push + (animated ? 0.5 : 0));
      } else {
        const m = o as THREE.Mesh;
        const materials = Array.isArray(m.material) ? m.material : [m.material];
        let materialKey = "";
        for (const material of materials) materialKey += `${material.uuid},`;
        const id = `m|${m.geometry.uuid}|${materialKey}|${m.castShadow}|${m.receiveShadow}|${o.renderOrder}|${groupOrder}`;
        batch = this.batch(id, () => ({ geometry: m.geometry, material: m.material, cast: m.castShadow, receive: m.receiveShadow, renderOrder: o.renderOrder, groupOrder, name: kind, depth: m.customDepthMaterial }));
        if (!m.geometry.boundingSphere) m.geometry.computeBoundingSphere();
        const sphere = m.geometry.boundingSphere as THREE.Sphere;
        const scale = o.matrixWorld.getMaxScaleOnAxis();
        batch.reach = Math.max(batch.reach, (sphere.center.length() + sphere.radius) * scale * (animated ? 1.2 : 1) + (animated ? 0.5 : 0));
      }
      parts.push({ batch, node: o, local, chain: blinks ? chain : undefined });
    });
    if (danger && !this.danger) {
      this.danger = this.batch("danger", () => ({ geometry: this.dangerGeometry, material: this.dangerMaterial, cast: false, receive: false, renderOrder: 0, groupOrder: 0, name: "danger" }));
    }
    const proto: PropProto = { key, kind, root, anim: anim === "pickup" || anim === "blink" ? anim : "", parts, danger };
    this.protos.set(key, proto);
    return proto;
  }

  /** Starts a frame: every batch empties (the live props are placed again). */
  begin(clock: number): void {
    this.clock = clock;
    for (let i = 0; i < this.list.length; i++) {
      const b = this.list[i];
      b.count = 0;
      b.minX = b.minY = b.minZ = Infinity;
      b.maxX = b.maxY = b.maxZ = -Infinity;
    }
  }

  /** One live prop: its root at (x, y, z), animated with phase `id` (as animateProp(object, clock, id) did). */
  place(proto: PropProto, id: number, x: number, y: number, z: number, box: Box): void {
    const anim = proto.anim;
    if (anim) {
      animateProp(proto.root, this.clock, id);
      if (anim === "pickup") proto.root.updateMatrixWorld(true);
    }
    const parts = proto.parts;
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      let shown = true;
      if (part.chain) for (let j = 0; j < part.chain.length; j++) if (!part.chain[j].visible) { shown = false; break; }
      this.write(part.batch, anim === "pickup" ? part.node.matrixWorld.elements : part.local, x, y, z, shown);
    }
    if (proto.danger && this.danger) {
      // the old child box: scale (w + 0.08, h + 0.08, max(0.3, d) + 0.08) at y = yBase + h / 2 inside the root
      _e.fill(0);
      _e[0] = box.width + 0.08; _e[5] = box.height + 0.08; _e[10] = Math.max(0.3, box.depth) + 0.08;
      _e[13] = box.yBase + box.height / 2; _e[15] = 1;
      this.write(this.danger, _e, x, y, z, true);
    }
  }

  /** Ends a frame: counts, upload ranges and one bounding sphere per batch round its instances. */
  end(): void {
    for (let i = 0; i < this.list.length; i++) {
      const b = this.list[i];
      const mesh = b.mesh;
      const n = b.count;
      mesh.count = n;
      mesh.visible = n > 0;
      if (n === 0) continue;
      mesh.instanceMatrix.clearUpdateRanges();
      mesh.instanceMatrix.addUpdateRange(0, n * 16);
      mesh.instanceMatrix.needsUpdate = true;
      b.bounds.center.set((b.minX + b.maxX) / 2, (b.minY + b.maxY) / 2, (b.minZ + b.maxZ) / 2);
      b.bounds.radius = Math.hypot(b.maxX - b.minX, b.maxY - b.minY, b.maxZ - b.minZ) / 2 + b.reach;
    }
  }

  /** Warm-up: draw everything wherever it is (true) or cull as usual (false). */
  setCulling(on: boolean): void {
    for (const b of this.list) b.mesh.frustumCulled = on;
  }

  /** labels every batch "entity:<kind>" (the first kind that made it) for the ?stats=deep owner tally */
  owners(owner: Map<THREE.Object3D, string>): void {
    for (const b of this.list) owner.set(b.mesh, `entity:${b.mesh.name}`);
  }

  dispose(): void {
    for (const b of this.list) b.mesh.dispose();
    for (const material of this.sprites.values()) material.dispose();
    this.quad.dispose();
    this.root.clear();
  }

  // ------------------------------------------------------------------------------------------------ internals

  private write(b: Batch, local: ArrayLike<number>, x: number, y: number, z: number, shown: boolean): void {
    if (b.count >= b.capacity) this.grow(b);
    const a = b.mesh.instanceMatrix.array as Float32Array;
    const o = b.count * 16;
    const tx = local[12] + x, ty = local[13] + y, tz = local[14] + z;
    if (shown) for (let k = 0; k < 12; k++) a[o + k] = local[k];
    else for (let k = 0; k < 12; k++) a[o + k] = 0;          // a blinking lamp that is off: collapsed to a point
    a[o + 12] = tx; a[o + 13] = ty; a[o + 14] = tz; a[o + 15] = 1;
    if (tx < b.minX) b.minX = tx; if (tx > b.maxX) b.maxX = tx;
    if (ty < b.minY) b.minY = ty; if (ty > b.maxY) b.maxY = ty;
    if (tz < b.minZ) b.minZ = tz; if (tz > b.maxZ) b.maxZ = tz;
    b.count++;
  }

  private batch(id: string, make: () => { geometry: THREE.BufferGeometry; material: THREE.Material | THREE.Material[]; cast: boolean; receive: boolean; renderOrder: number; groupOrder: number; name: string; depth?: THREE.Material }): Batch {
    let b = this.batches.get(id);
    if (b) return b;
    const spec = make();
    const capacity = 16;
    const mesh = new THREE.InstancedMesh(spec.geometry, spec.material, capacity);
    mesh.name = spec.name;
    mesh.castShadow = spec.cast;
    mesh.receiveShadow = spec.receive;
    mesh.renderOrder = spec.renderOrder;
    if (spec.depth) mesh.customDepthMaterial = spec.depth;
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    mesh.count = 0;
    mesh.visible = false;
    mesh.matrixAutoUpdate = false;
    const bounds = new THREE.Sphere(new THREE.Vector3(), 0);
    mesh.boundingSphere = bounds;
    b = { mesh, count: 0, capacity, reach: 0, minX: Infinity, minY: Infinity, minZ: Infinity, maxX: -Infinity, maxY: -Infinity, maxZ: -Infinity, bounds };
    this.group(spec.groupOrder).add(mesh);
    this.batches.set(id, b);
    this.list.push(b);
    return b;
  }

  /** the holder that gives its batches three.js's group order (a part's nearest Group's renderOrder) */
  private group(order: number): THREE.Group {
    let g = this.groups.get(order);
    if (!g) {
      g = new THREE.Group();
      g.renderOrder = order;
      g.matrixAutoUpdate = false;
      this.groups.set(order, g);
      this.root.add(g);
    }
    return g;
  }

  /** Doubles a batch's capacity mid-frame: a new instance buffer with the matrices written so far. */
  private grow(b: Batch): void {
    const capacity = b.capacity * 2;
    const old = b.mesh;
    const mesh = new THREE.InstancedMesh(old.geometry, old.material, capacity);
    mesh.name = old.name;
    mesh.castShadow = old.castShadow;
    mesh.receiveShadow = old.receiveShadow;
    mesh.renderOrder = old.renderOrder;
    mesh.customDepthMaterial = old.customDepthMaterial;
    mesh.frustumCulled = old.frustumCulled;
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    mesh.matrixAutoUpdate = false;
    mesh.boundingSphere = b.bounds;
    (mesh.instanceMatrix.array as Float32Array).set((old.instanceMatrix.array as Float32Array).subarray(0, b.count * 16));
    mesh.count = 0;
    mesh.visible = false;
    const parent = old.parent;
    parent?.add(mesh);
    parent?.remove(old);
    old.dispose();
    b.mesh = mesh;
    b.capacity = capacity;
  }
}

